import axios from "axios";
import { isOnline } from "@/utils/connectivity";
import {
  cachePosSnapshot,
  getCachedPosSnapshot,
  idbDelete,
  idbGetAll,
  idbPut,
  toStorable,
} from "@/utils/posOffline/db";
import { getOrCreateDeviceUuid, generateUuid } from "@/utils/posOffline/device";
import { countPendingOutbox, listOutbox, updateOutboxEntry } from "@/utils/posOffline/sync";

const OP_ORDER = {
  abrir: 10,
  movimiento: 20,
  cerrar: 90,
};

function sameUsuarioOffline(a, b) {
  return String(a ?? "") === String(b ?? "");
}

async function persistCajaEstado(usuario, estado) {
  if (!usuario || !estado) return;
  await cachePosSnapshot(`caja:${usuario}`, {
    ...estado,
    cached_at: new Date().toISOString(),
  });
}

function notifyCajaOutboxChange() {
  if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
    window.dispatchEvent(new Event("caja:outbox-updated"));
  }
}

export function isLocalCajaId(id) {
  if (id == null || id === "") return false;
  const s = String(id);
  return s.startsWith("local_") || Number(id) < 0;
}

function round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

function normalizarMetodoCaja(medio) {
  const m = String(medio || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  if (!m) return "efectivo";
  if (m.includes("mixt") || m.includes("combin")) return "mixto";
  if (m.includes("efectivo") || m === "cash") return "efectivo";
  if (
    m.includes("debito") ||
    m.includes("credito") ||
    m.includes("tarjeta") ||
    m.includes("card")
  ) {
    return "tarjeta";
  }
  if (m.includes("transfer")) return "transferencia";
  if (m.includes("qr") || m.includes("mercado") || m.includes("billetera")) return "qr";
  if (m.includes("pendiente")) return "pendiente";
  return "efectivo";
}

function impactoEfectivoMovimiento(tipo, monto) {
  const m = round2(monto);
  if (tipo === "INGRESO") return m;
  if (tipo === "EGRESO" || tipo === "RETIRO") return -m;
  if (tipo === "AJUSTE") return m;
  return 0;
}

export async function listOutboxCajas(usuario, statusFilter) {
  const all = (await idbGetAll("outbox_cajas")) || [];
  let rows = usuario
    ? all.filter((r) => sameUsuarioOffline(r.usuario, usuario))
    : all;
  if (statusFilter) {
    rows = rows.filter((r) => r.sync_status === statusFilter);
  }
  return rows.sort((a, b) =>
    String(a.created_at || "").localeCompare(String(b.created_at || "")),
  );
}

export async function countPendingCajaOutbox(usuario) {
  const rows = await listOutboxCajas(usuario);
  return rows.filter((r) =>
    ["pending", "failed", "syncing"].includes(r.sync_status),
  ).length;
}

export async function updateCajaOutboxEntry(id, patch) {
  const all = await idbGetAll("outbox_cajas");
  const row = all.find((x) => x.client_mutation_id === id);
  if (!row) return null;
  const next = toStorable({ ...row, ...patch });
  await idbPut("outbox_cajas", next);
  notifyCajaOutboxChange();
  return next;
}

/**
 * Encola abrir | movimiento | cerrar.
 * - abrir: genera local_caja_id si no viene.
 * - cerrar: reemplaza un cerrar pendiente previo de la misma caja.
 */
export async function enqueueCajaOp({
  tipo,
  usuario,
  caja_id = null,
  payload = {},
  client_mutation_id = null,
}) {
  const device_uuid = await getOrCreateDeviceUuid();
  let mutationId = client_mutation_id || generateUuid();
  let finalPayload = { ...(payload || {}) };
  let cajaId = caja_id;

  if (tipo === "abrir") {
    if (!finalPayload.local_caja_id) {
      finalPayload.local_caja_id = `local_${generateUuid()}`;
    }
    cajaId = finalPayload.local_caja_id;
    // Una sola apertura pendiente por usuario.
    const existing = await listOutboxCajas(usuario);
    for (const row of existing) {
      if (
        row.tipo === "abrir" &&
        ["pending", "failed"].includes(row.sync_status)
      ) {
        await idbDelete("outbox_cajas", row.client_mutation_id);
      }
    }
  }

  if (tipo === "cerrar" && cajaId != null) {
    const existing = await listOutboxCajas(usuario);
    for (const row of existing) {
      if (
        row.tipo === "cerrar" &&
        String(row.caja_id) === String(cajaId) &&
        ["pending", "failed"].includes(row.sync_status)
      ) {
        await idbDelete("outbox_cajas", row.client_mutation_id);
      }
    }
  }

  const entry = toStorable({
    client_mutation_id: mutationId,
    device_uuid,
    tipo,
    caja_id: cajaId,
    usuario,
    payload: finalPayload,
    sync_status: "pending",
    created_at: new Date().toISOString(),
    last_error: null,
  });
  await idbPut("outbox_cajas", entry);
  notifyCajaOutboxChange();
  return entry;
}

export async function remapCajaIdEverywhere(usuario, fromId, toId) {
  if (fromId == null || toId == null || String(fromId) === String(toId)) return;

  const snap = (await getCachedPosSnapshot(`caja:${usuario}`)) || {};
  if (snap.cajaId != null && String(snap.cajaId) === String(fromId)) {
    const caja = snap.caja
      ? { ...snap.caja, caja_id: toId, _offlineTemp: false }
      : snap.caja;
    await persistCajaEstado(usuario, {
      ...snap,
      cajaId: toId,
      caja,
      abierta: snap.abierta !== false,
    });
  }

  const cajaOps = await listOutboxCajas(usuario);
  for (const op of cajaOps) {
    if (String(op.caja_id) !== String(fromId)) continue;
    const payload = { ...(op.payload || {}) };
    if (payload.local_caja_id && String(payload.local_caja_id) === String(fromId)) {
      payload.local_caja_id = toId;
    }
    await updateCajaOutboxEntry(op.client_mutation_id, {
      caja_id: toId,
      payload,
    });
  }

  const sales = await listOutbox();
  for (const sale of sales) {
    const payload = sale.payload || {};
    if (payload.expected_caja_id == null) continue;
    if (String(payload.expected_caja_id) !== String(fromId)) continue;
    await updateOutboxEntry(sale.client_mutation_id, {
      payload: { ...payload, expected_caja_id: toId },
    });
  }

  try {
    const mesas = (await idbGetAll("outbox_mesas")) || [];
    for (const op of mesas) {
      if (!sameUsuarioOffline(op.usuario, usuario)) continue;
      const payload = op.payload || {};
      if (payload.expected_caja_id == null) continue;
      if (String(payload.expected_caja_id) !== String(fromId)) continue;
      await idbPut(
        "outbox_mesas",
        toStorable({
          ...op,
          payload: { ...payload, expected_caja_id: toId },
        }),
      );
    }
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event("mesas:outbox-updated"));
    }
  } catch (_) {
    /* outbox_mesas puede no existir en DBs viejas */
  }
}

function emptyResumen(cajaId) {
  return {
    caja_id: cajaId,
    estado: "ABIERTA",
    monto_inicial: 0,
    total_vendido: 0,
    ventas_efectivo: 0,
    ventas_tarjeta: 0,
    ventas_transferencia: 0,
    ventas_qr: 0,
    ventas_mixto: 0,
    cantidad_pedidos: 0,
    ingresos_manuales: 0,
    egresos: 0,
    retiros: 0,
    ajustes: 0,
    efectivo_esperado: 0,
  };
}

function applySaleToResumen(resumen, medioPago, total) {
  const t = round2(total);
  const metodo = normalizarMetodoCaja(medioPago);
  if (metodo === "pendiente") return;
  resumen.total_vendido = round2(resumen.total_vendido + t);
  resumen.cantidad_pedidos += 1;
  if (metodo === "efectivo") resumen.ventas_efectivo = round2(resumen.ventas_efectivo + t);
  else if (metodo === "tarjeta") resumen.ventas_tarjeta = round2(resumen.ventas_tarjeta + t);
  else if (metodo === "transferencia") {
    resumen.ventas_transferencia = round2(resumen.ventas_transferencia + t);
  } else if (metodo === "qr") resumen.ventas_qr = round2(resumen.ventas_qr + t);
  else if (metodo === "mixto") resumen.ventas_mixto = round2(resumen.ventas_mixto + t);
  else resumen.ventas_efectivo = round2(resumen.ventas_efectivo + t);
}

/** Resumen local = snapshot server + ventas pending + movimientos pending. */
export async function computeResumenCajaLocal(usuario) {
  const snap = (await getCachedPosSnapshot(`caja:${usuario}`)) || {};
  const cajaId = snap.cajaId || (snap.caja && snap.caja.caja_id) || null;
  const serverBase = snap.resumen_server || null;
  const base = {
    ...emptyResumen(cajaId),
    ...(serverBase || {}),
    monto_inicial: round2(
      (serverBase && serverBase.monto_inicial) ??
        (snap.caja && snap.caja.monto_inicial) ??
        0,
    ),
  };

  // Si no hay resumen_server (apertura solo local), partir de cero + monto inicial.
  if (!serverBase) {
    base.total_vendido = 0;
    base.ventas_efectivo = 0;
    base.ventas_tarjeta = 0;
    base.ventas_transferencia = 0;
    base.ventas_qr = 0;
    base.ventas_mixto = 0;
    base.cantidad_pedidos = 0;
    base.ingresos_manuales = 0;
    base.egresos = 0;
    base.retiros = 0;
    base.ajustes = 0;
  }

  // Ventas POS pendientes vinculadas a esta caja (o sin expected si es la abierta).
  const sales = await listOutbox();
  for (const sale of sales) {
    if (!["pending", "failed", "caja_mismatch", "syncing"].includes(sale.sync_status)) {
      continue;
    }
    const payload = sale.payload || {};
    const expected = payload.expected_caja_id;
    if (
      expected != null &&
      cajaId != null &&
      String(expected) !== String(cajaId)
    ) {
      continue;
    }
    applySaleToResumen(base, payload.medio_pago, payload.total);
  }

  // Cobros de mesa pendientes.
  try {
    const mesas = (await idbGetAll("outbox_mesas")) || [];
    for (const op of mesas) {
      if (!sameUsuarioOffline(op.usuario, usuario)) continue;
      if (op.tipo !== "cobrar") continue;
      if (!["pending", "failed", "caja_mismatch", "syncing"].includes(op.sync_status)) {
        continue;
      }
      const payload = op.payload || {};
      const expected = payload.expected_caja_id;
      if (
        expected != null &&
        cajaId != null &&
        String(expected) !== String(cajaId)
      ) {
        continue;
      }
      const items = Array.isArray(payload.items) ? payload.items : [];
      const total = round2(
        items.reduce(
          (acc, it) =>
            acc +
            (Number(it.producto_precio) || 0) *
              (Number(it.cantidadSeleccionada) || 0),
          0,
        ),
      );
      applySaleToResumen(base, payload.medio_pago, total);
    }
  } catch (_) {
    /* ignore */
  }

  const movOps = await listOutboxCajas(usuario);
  for (const op of movOps) {
    if (op.tipo !== "movimiento") continue;
    if (!["pending", "failed", "syncing"].includes(op.sync_status)) continue;
    if (cajaId != null && String(op.caja_id) !== String(cajaId)) continue;
    const p = op.payload || {};
    const tipo = String(p.tipo || "").toUpperCase();
    const monto = round2(p.monto);
    if (tipo === "INGRESO") base.ingresos_manuales = round2(base.ingresos_manuales + monto);
    else if (tipo === "EGRESO") base.egresos = round2(base.egresos + monto);
    else if (tipo === "RETIRO") base.retiros = round2(base.retiros + monto);
    else if (tipo === "AJUSTE") {
      base.ajustes = round2(base.ajustes + impactoEfectivoMovimiento("AJUSTE", monto));
    }
  }

  base.efectivo_esperado = round2(
    base.monto_inicial +
      base.ventas_efectivo +
      base.ingresos_manuales -
      base.egresos -
      base.retiros +
      base.ajustes,
  );
  base.caja_id = cajaId;
  base.estado = snap.abierta === false ? "CERRADA" : "ABIERTA";
  return base;
}

/** Aplica apertura local al snapshot (tras enqueue o online). */
export async function applyLocalAbrir(usuario, { monto_inicial, observacion, local_caja_id, actor_login, actor_nombre }) {
  const localId = local_caja_id || `local_${generateUuid()}`;
  const caja = {
    caja_id: localId,
    monto_inicial: round2(monto_inicial || 0),
    observacion_apertura: observacion || null,
    estado: "ABIERTA",
    fecha_apertura: new Date().toISOString(),
    actor_apertura_login: actor_login || null,
    actor_apertura_nombre: actor_nombre || null,
    _offlineTemp: true,
  };
  const resumen = {
    ...emptyResumen(localId),
    monto_inicial: caja.monto_inicial,
    efectivo_esperado: caja.monto_inicial,
    estado: "ABIERTA",
  };
  await persistCajaEstado(usuario, {
    requerida: true,
    abierta: true,
    cajaId: localId,
    caja,
    resumen,
    resumen_server: null,
  });
  return { caja, resumen, cajaId: localId };
}

export async function applyLocalMovimiento(usuario, { tipo, monto, descripcion }) {
  const snap = (await getCachedPosSnapshot(`caja:${usuario}`)) || {};
  if (!snap.abierta || !snap.cajaId) {
    throw new Error("No hay caja abierta.");
  }
  const movs = Array.isArray(snap.movimientos_local) ? [...snap.movimientos_local] : [];
  movs.push({
    movimiento_id: `local_${generateUuid()}`,
    caja_id: snap.cajaId,
    tipo: String(tipo || "").toUpperCase(),
    monto: round2(monto),
    descripcion: String(descripcion || "").trim(),
    created_at: new Date().toISOString(),
    _offline: true,
  });
  await persistCajaEstado(usuario, { ...snap, movimientos_local: movs });
  const resumen = await computeResumenCajaLocal(usuario);
  await persistCajaEstado(usuario, { ...snap, movimientos_local: movs, resumen });
  return resumen;
}

export async function applyLocalCerrar(usuario) {
  const snap = (await getCachedPosSnapshot(`caja:${usuario}`)) || {};
  await persistCajaEstado(usuario, {
    ...snap,
    abierta: false,
    cajaId: null,
    caja: snap.caja ? { ...snap.caja, estado: "CERRADA" } : null,
    resumen: null,
    movimientos_local: [],
  });
}

export async function countPendingVentasDispositivo(usuario) {
  const [sales, mesasCount] = await Promise.all([
    countPendingOutbox().catch(() => 0),
    (async () => {
      try {
        const { countPendingMesasCobrosPedidos } = await import(
          "@/utils/posOffline/mesasSync"
        );
        return countPendingMesasCobrosPedidos(usuario);
      } catch (_) {
        return 0;
      }
    })(),
  ]);
  return Number(sales || 0) + Number(mesasCount || 0);
}

function sortOps(ops) {
  return [...ops].sort((a, b) => {
    const oa = OP_ORDER[a.tipo] || 100;
    const ob = OP_ORDER[b.tipo] || 100;
    if (oa !== ob) return oa - ob;
    return String(a.created_at || "").localeCompare(String(b.created_at || ""));
  });
}

async function replayAbrir(op) {
  const payload = op.payload || {};
  try {
    const { data } = await axios.post("/cajas/abrir", {
      monto_inicial: payload.monto_inicial || 0,
      observacion_apertura: payload.observacion || payload.observacion_apertura || null,
      client_mutation_id: op.client_mutation_id,
      device_uuid: op.device_uuid,
    });
    return data;
  } catch (e) {
    // Ya hay caja abierta en server: adoptar esa.
    if (e.response && e.response.status === 409 && e.response.data && e.response.data.caja) {
      return e.response.data;
    }
    throw e;
  }
}

async function replayMovimiento(op) {
  const cajaId = op.caja_id;
  if (isLocalCajaId(cajaId)) {
    throw new Error("La caja local todavía no se sincronizó.");
  }
  const payload = op.payload || {};
  const { data } = await axios.post(`/cajas/${cajaId}/movimientos`, {
    tipo: payload.tipo,
    monto: payload.monto,
    descripcion: payload.descripcion,
    client_mutation_id: op.client_mutation_id,
    device_uuid: op.device_uuid,
  });
  return data;
}

async function replayCerrar(op) {
  const cajaId = op.caja_id;
  if (isLocalCajaId(cajaId)) {
    throw new Error("La caja local todavía no se sincronizó.");
  }
  const payload = op.payload || {};
  const { data } = await axios.post(`/cajas/${cajaId}/cerrar`, {
    efectivo_contado: payload.efectivo_contado,
    observacion_cierre: payload.observacion || payload.observacion_cierre || null,
    client_mutation_id: op.client_mutation_id,
    device_uuid: op.device_uuid,
  });
  return data;
}

/** Sync abrir + movimientos (antes de ventas POS/Mesas). */
export async function syncOutboxCajasPreSales(usuario) {
  if (!isOnline() || !usuario) return { syncedCount: 0, conflicts: 0 };

  const queue = await listOutboxCajas(usuario);
  const pending = sortOps(
    queue.filter(
      (op) =>
        (op.tipo === "abrir" || op.tipo === "movimiento") &&
        ["pending", "failed"].includes(op.sync_status),
    ),
  );

  let syncedCount = 0;
  let conflicts = 0;

  for (const op of pending) {
    try {
      await updateCajaOutboxEntry(op.client_mutation_id, {
        sync_status: "syncing",
        last_error: null,
      });

      if (op.tipo === "abrir") {
        const data = await replayAbrir(op);
        const realId = data.caja && data.caja.caja_id;
        const localId = op.caja_id || (op.payload && op.payload.local_caja_id);
        if (realId != null && localId != null) {
          await remapCajaIdEverywhere(usuario, localId, realId);
          for (const later of pending) {
            if (String(later.caja_id) === String(localId)) {
              later.caja_id = realId;
            }
          }
        }
        if (data.caja) {
          await persistCajaEstado(usuario, {
            requerida: true,
            abierta: true,
            cajaId: data.caja.caja_id,
            caja: data.caja,
            resumen: data.resumen || null,
            resumen_server: data.resumen || null,
          });
        }
      } else if (op.tipo === "movimiento") {
        await replayMovimiento(op);
      }

      await idbDelete("outbox_cajas", op.client_mutation_id);
      syncedCount++;
    } catch (e) {
      const data = e.response && e.response.data;
      await updateCajaOutboxEntry(op.client_mutation_id, {
        sync_status: "failed",
        last_error: (data && data.message) || (e && e.message) || "Error",
      });
      conflicts++;
    }
  }

  if (syncedCount > 0) {
    try {
      const { data } = await axios.get("/cajas/actual");
      if (data) {
        await persistCajaEstado(usuario, {
          requerida: true,
          abierta: !!data.abierta,
          cajaId: data.caja && data.caja.caja_id != null ? data.caja.caja_id : null,
          caja: data.caja || null,
          resumen: data.resumen || null,
          resumen_server: data.abierta ? data.resumen || null : null,
        });
      }
    } catch (_) {
      /* ignore */
    }
    notifyCajaOutboxChange();
  }

  return { syncedCount, conflicts };
}

/** Sync cerrar (después de ventas), solo si no quedan ventas pendientes. */
export async function syncOutboxCajasPostSales(usuario) {
  if (!isOnline() || !usuario) return { syncedCount: 0, conflicts: 0, skipped: false };

  const ventasPendientes = await countPendingVentasDispositivo(usuario);
  if (ventasPendientes > 0) {
    return { syncedCount: 0, conflicts: 0, skipped: true, ventasPendientes };
  }

  const queue = await listOutboxCajas(usuario);
  const pending = sortOps(
    queue.filter(
      (op) =>
        op.tipo === "cerrar" && ["pending", "failed"].includes(op.sync_status),
    ),
  );

  let syncedCount = 0;
  let conflicts = 0;

  for (const op of pending) {
    try {
      await updateCajaOutboxEntry(op.client_mutation_id, {
        sync_status: "syncing",
        last_error: null,
      });
      await replayCerrar(op);
      await idbDelete("outbox_cajas", op.client_mutation_id);
      await persistCajaEstado(usuario, {
        requerida: true,
        abierta: false,
        cajaId: null,
        caja: null,
        resumen: null,
        movimientos_local: [],
      });
      syncedCount++;
    } catch (e) {
      const data = e.response && e.response.data;
      // Caja ya cerrada con misma mutation o ya cerrada: tratar como ok si 409
      if (e.response && e.response.status === 409) {
        await idbDelete("outbox_cajas", op.client_mutation_id);
        await persistCajaEstado(usuario, {
          requerida: true,
          abierta: false,
          cajaId: null,
          caja: null,
          resumen: null,
          movimientos_local: [],
        });
        syncedCount++;
        continue;
      }
      await updateCajaOutboxEntry(op.client_mutation_id, {
        sync_status: "failed",
        last_error: (data && data.message) || (e && e.message) || "Error",
      });
      conflicts++;
    }
  }

  if (syncedCount > 0 || conflicts > 0) notifyCajaOutboxChange();
  return { syncedCount, conflicts, skipped: false };
}

export async function syncOutboxCajas(usuario) {
  const pre = await syncOutboxCajasPreSales(usuario);
  const post = await syncOutboxCajasPostSales(usuario);
  return {
    syncedCount: pre.syncedCount + post.syncedCount,
    conflicts: pre.conflicts + post.conflicts,
    pre,
    post,
  };
}
